/*
 * Draws a name as crocheted yarn, from a pre-generated glyph atlas.
 *
 * Nothing is generated at request time. Each letter was produced once as a
 * photograph of real-looking yarn; this lays them out and recolours them.
 *
 * Layout comes from the source font's own metrics, baked into a JSON table at
 * build time. Guessing where letters join by inspecting pixels does not work —
 * the leftmost pixel of an 'l' is the top of its ascender, not its baseline
 * connector — so we place each glyph in the box the font specifies and let the
 * built-in entry and exit strokes meet on their own.
 *
 * Colour comes from tinting the cream atlas. The fibre detail lives in the
 * luminance, so dividing by the cream's own base tone gives a neutral shading
 * map that can be multiplied by any yarn colour with the stitches intact. A
 * plain multiply would muddy it, because cream is not white.
 */

class YarnRenderer {
  constructor(config) {
    this.urls = config.atlas || {};
    this.metricsUrl = config.metricsUrl;
    this.creamBase = config.creamBase || [231.6, 225.0, 211.7];
    this.metrics = null;
    this.rawCache = new Map();    // char -> HTMLImageElement
    this.tintCache = new Map();   // char|hex -> canvas
    this.pending = new Map();
  }

  async ready() {
    if (this.metrics) return this.metrics;
    if (!this.metricsReq) {
      this.metricsReq = fetch(this.metricsUrl)
        .then((r) => r.json())
        .then((m) => (this.metrics = m));
    }
    return this.metricsReq;
  }

  key(ch, isFirst) {
    return (isFirst ? 'cap:' : 'low:') + ch;
  }

  loadGlyph(ch, isFirst) {
    const k = this.key(ch, isFirst);
    if (this.rawCache.has(k)) return Promise.resolve(this.rawCache.get(k));
    if (this.pending.has(k)) return this.pending.get(k);

    const url = this.urls[k];
    if (!url) return Promise.resolve(null);

    const p = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.rawCache.set(k, img); resolve(img); };
      img.onerror = () => resolve(null);
      img.src = url;
    }).finally(() => this.pending.delete(k));

    this.pending.set(k, p);
    return p;
  }

  /* Recolour one glyph and cache the result per colour. */
  tinted(ch, isFirst, hex) {
    const k = this.key(ch, isFirst) + '|' + hex;
    if (this.tintCache.has(k)) return this.tintCache.get(k);

    const img = this.rawCache.get(this.key(ch, isFirst));
    if (!img) return null;

    const c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);

    const target = [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16)
    ];
    // Cream is close enough to the target that tinting would be a no-op.
    const isCream = Math.abs(target[0] - this.creamBase[0]) < 14 &&
                    Math.abs(target[1] - this.creamBase[1]) < 14 &&
                    Math.abs(target[2] - this.creamBase[2]) < 14;

    if (!isCream) {
      const d = ctx.getImageData(0, 0, c.width, c.height);
      const p = d.data;
      const [br, bg, bb] = this.creamBase;
      for (let i = 0; i < p.length; i += 4) {
        if (p[i + 3] === 0) continue;
        p[i]     = Math.min(255, (p[i]     / br) * target[0]);
        p[i + 1] = Math.min(255, (p[i + 1] / bg) * target[1]);
        p[i + 2] = Math.min(255, (p[i + 2] / bb) * target[2]);
      }
      ctx.putImageData(d, 0, 0);
    }

    this.tintCache.set(k, c);
    return c;
  }

  /*
   * Lay the word out in em units. Returns the boxes plus the overall extent,
   * so the caller can decide the on-screen scale.
   */
  layout(word) {
    const g = this.metrics.glyphs;
    const items = [];
    let pen = 0, top = Infinity, bottom = -Infinity, right = 0;

    for (let i = 0; i < word.length; i++) {
      const ch = word[i];
      const m = g[ch];
      if (!m) continue;
      const [x0, y0, x1, y1] = m.bbox;
      if (x1 > x0 && y1 > y0) {
        items.push({ ch, isFirst: i === 0, x: pen + x0, y: y0, w: x1 - x0, h: y1 - y0 });
        top = Math.min(top, y0);
        bottom = Math.max(bottom, y1);
        right = Math.max(right, pen + x1);
      }
      pen += m.adv;
    }
    if (!items.length) return null;
    const left = Math.min(...items.map((it) => it.x));
    return { items, left, top, width: right - left, height: bottom - top };
  }

  /*
   * Draw `word` into `canvas`, sized so one em is `emPx` device pixels, and
   * shrunk if that would exceed maxWidthPx. Returns the CSS size to apply.
   */
  async draw(canvas, word, hex, opts) {
    await this.ready();
    /*
     * The first glyph always comes from the capitals atlas, so a name typed in
     * lower case looked up a key that does not exist and the letter vanished —
     * "freya" came out as "reya". These are children's names; the first letter
     * is a capital either way.
     */
    let clean = (word || '').trim();
    if (!clean) return null;
    clean = clean.charAt(0).toUpperCase() + clean.slice(1);

    await Promise.all(
      Array.from(new Set(clean.split('').map((c, i) => this.key(c, i === 0) + ' ' + c)))
        .map((s) => {
          const [k, ch] = s.split(' ');
          return this.loadGlyph(ch, k.startsWith('cap'));
        })
    );

    const lay = this.layout(clean);
    if (!lay) return null;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let scale = opts.emPx / this.metrics.em;
    if (lay.width * scale > opts.maxWidthPx) scale = opts.maxWidthPx / lay.width;

    const pad = Math.ceil((opts.shadowBlur + Math.abs(opts.shadowY) + 4) * scale + 6);
    const w = Math.ceil(lay.width * scale) + pad * 2;
    const h = Math.ceil(lay.height * scale) + pad * 2;

    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    ctx.shadowColor = opts.shadowColor;
    ctx.shadowBlur = opts.shadowBlur * scale;
    ctx.shadowOffsetX = opts.shadowX * scale;
    ctx.shadowOffsetY = opts.shadowY * scale;

    for (const it of lay.items) {
      const art = this.tinted(it.ch, it.isFirst, hex);
      if (!art) continue;
      ctx.drawImage(
        art,
        pad + (it.x - lay.left) * scale,
        pad + (it.y - lay.top) * scale,
        it.w * scale,
        it.h * scale
      );
    }
    return { cssWidth: w, cssHeight: h };
  }
}

window.YarnRenderer = YarnRenderer;
